/* eslint-disable filenames/match-exported */
import type ModelType from 'echarts/types/src/model/Model';
import Model from 'echarts/lib/model/Model';
import type SeriesModelType from 'echarts/types/src/model/Series';
import SeriesModel from 'echarts/lib/model/Series';
// eslint-disable-next-line import/no-named-default
import type { default as TreeType, TreeNode } from 'echarts/types/src/data/Tree';
import Tree from 'echarts/lib/data/Tree';
import type SeriesData from 'echarts/types/src/data/SeriesData';
import type {
  SeriesOption,
  BoxLayoutOptionMixin,
  RoamOptionMixin
} from 'echarts/types/src/util/types';
import type { LayoutRect } from 'echarts/types/src/util/layout';
import { helper, List } from 'echarts/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import type { GroupProps, PathProps, PathState, TextProps } from 'zrender';
import { NODE_TYPE, STATE } from './twoWayTreeLayout';

type State = {
  [key in typeof STATE[keyof typeof STATE]]: PathState;
};

export type RootNodeRenderOption = {
  /** 项目（节点）的渲染配置 */
  item?: GroupProps & {
    /** 子元素节点 */
    children?: {
      [name: string]: PathProps & { type?: string } & { states?: State };
      /** 主文本元素 */
      text?: TextProps & { states?: State };
      /** 副文本元素 */
      attachment?: TextProps & { states?: State };
    };
  };
  /** 线（边）的渲染配置 */
  line?: {
    /** 子元素节点 */
    children?: {
      [name: string]: PathProps & { type?: string } & { states?: State };
      line?: PathProps & { states?: State };
    };
  };
};
export type NormalNodeRenderOption = RootNodeRenderOption;
export type GroupNodeRenderOption = {
  /** 项目（节点）的渲染配置 */
  item?: GroupProps & {
    /** 子元素节点 */
    children?: {
      [name: string]: PathProps & { type?: string } & { states?: State };
      /** 标签文本元素 */
      labelText?: TextProps & { states?: State };
    };
  };
  /** 线（边）的渲染配置 */
  line?: {
    /** 子元素节点 */
    children?: {
      [name: string]: PathProps & { type?: string } & { states?: State };
      line?: PathProps & { states?: State };
    };
  };
};

type RenderOption = {
  [NODE_TYPE.ROOT]?: RootNodeRenderOption;
  [NODE_TYPE.NORMAL]?: NormalNodeRenderOption;
  [NODE_TYPE.GROUP]?: GroupNodeRenderOption;
};

export interface TwoWayTreeSeriesNodeItemOption {
  type: typeof NODE_TYPE[keyof typeof NODE_TYPE];
  /** 节点的位置（左子树，还是右子树） */
  position?: 'left' | 'right';
  /** 节点的名称，用来标识每一个节点 */
  name?: string;
  /** 节点的值，在 tooltip 中显示 */
  value?: string;
  /** 是否折叠起来（只对 type = 'group' 的节点有效） */
  collapsed?: boolean;
  /** v0.0.23+ 是否默认选中（只对 type = 'root' | 'normal' 的节点有效），且只在该节点的父节点默认展开时有效 */
  selected?: boolean;
  /** 渲染配置 */
  render: RootNodeRenderOption | GroupNodeRenderOption;
  /** 孩子节点 */
  children?: TwoWayTreeSeriesNodeItemOption[];
}

export interface TwoWayTreeSeriesOption
  extends SeriesOption,
    BoxLayoutOptionMixin,
    RoamOptionMixin {
  type: 'dvTwoWayTree';
  data: TwoWayTreeSeriesNodeItemOption;
  /** 画布的背景色 */
  backgroundColor?: string;
  /** 是否支持折叠和展开 */
  expandAndCollapse?: boolean;
  /** 初始展开的树深度（根节点的深度可认为为 0） */
  initialTreeDepth?: number;
  /** 选中时高亮祖先节点 */
  selectedHighlightAncestor?: boolean;
  /** v0.2.2+ 点击 group 类型节点之后清除选中状态 */
  clearSelectedAfterClickGroupNode?: boolean;
  /** v0.2.2+ 点击交互后聚焦的画布视区中心位置 */
  viewCenterAfterClick?:
    | [number, number]
    | ((arg: {
        node: TreeNode;
        data: TwoWayTreeSeriesNodeItemOption;
        isExpand?: boolean;
        isSelected?: boolean;
      }) => [number, number]);
  /** 节点的渲染配置 */
  render?: RenderOption;
}

// eslint-disable-next-line no-use-before-define
export type TwoWayTreeSeriesModelClazz = TwoWayTreeSeriesModel &
  SeriesModelType<TwoWayTreeSeriesOption>;

/**
 * 设置为初始状态
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function setToInitialState(seriesModel: TwoWayTreeSeriesModelClazz) {
  const trees = [seriesModel.leftTree, seriesModel.rightTree];
  const selectedNodes: TreeNode[] = [];
  const { expandAndCollapse, initialTreeDepth } = seriesModel.option;
  const expandTreeDepth = expandAndCollapse && initialTreeDepth >= 0 ? initialTreeDepth : Infinity;

  trees.forEach((tree, index) => {
    tree.root.eachNode('preorder', (node: TreeNode) => {
      const item = node.hostTree.data.getRawDataItem(
        node.dataIndex
      ) as TwoWayTreeSeriesNodeItemOption;

      // Add item.collapsed != null, because users can collapse node original in the series.data.
      // eslint-disable-next-line no-param-reassign
      node.isExpand =
        item && typeof item.collapsed !== 'undefined'
          ? !item.collapsed
          : node.depth <= expandTreeDepth;

      // ! 常规节点必须展开，否则其子节点（组节点）无法交互
      if (item.type === NODE_TYPE.NORMAL) {
        // eslint-disable-next-line no-param-reassign
        node.isExpand = true;
      }

      if (
        (item.type === NODE_TYPE.ROOT || item.type === NODE_TYPE.NORMAL) &&
        // ! 右子树的 root 节点布局时会设置 noRender 标志位，所以只有左子树的 root 节点需要处理选中状态
        !(index === 1 && node.depth === 1)
      ) {
        // * 初始化选中状态
        node.__selected = !!item.selected;
        if (node.__selected) {
          selectedNodes.push(node);
        }
      }
    });
  });

  // ! 目前支持单选
  selectedNodes.sort((a, b) => a.depth - b.depth);
  let find = false;

  while (!find) {
    const node = selectedNodes.pop();
    if (!node || !node.parentNode || node.parentNode.isExpand) {
      find = true;
    } else {
      node.__selected = false;
    }
  }

  selectedNodes.forEach(node => {
    node.__selected = false;
  });
}

// see src/chart/tree/TreeSeries.ts
// eslint-disable-next-line sonarjs/cognitive-complexity
function buildTree(
  dataRoot: TwoWayTreeSeriesNodeItemOption,
  hostModel: TwoWayTreeSeriesModelClazz
) {
  const leaves = hostModel.option.leaves || {};

  const leavesModel = new Model(leaves, hostModel, hostModel.ecModel) as ModelType;

  function beforeLink(nodeData: SeriesData) {
    nodeData.wrapMethod('getItemModel', (model: ModelType, idx: number) => {
      const node = tree.getNodeByDataIndex(idx);
      if (!(node && node.children.length && node.isExpand)) {
        model.parentModel = leavesModel;
      }
      return model;
    });
  }

  const tree = Tree.createTree(
    dataRoot,
    hostModel,
    beforeLink
  ) as TreeType<TwoWayTreeSeriesModelClazz>;

  return tree;
}

export default class TwoWayTreeSeriesModel extends SeriesModel<TwoWayTreeSeriesOption> {
  //
  static readonly type = 'series.dvTwoWayTree';

  readonly type = TwoWayTreeSeriesModel.type;

  static defaultOption: TwoWayTreeSeriesOption = {
    //
    preventAutoZ: true,
    coordinateSystemUsage: 'box',

    // the position of the whole view
    left: '5%',
    top: '5%',
    right: '5%',
    bottom: '5%',

    /**
     * If enable roam. can be specified 'scale' or 'move'
     * see module:component/helper/RoamController.
     */
    // roam: boolean | 'pan' | 'move' | 'zoom' | 'scale'
    roam: true,
    roamTrigger: 'global',
    scaleLimit: {
      min: 0.5,
      max: 2
    },

    backgroundColor: '#f5f5f5',

    expandAndCollapse: true,
    initialTreeDepth: 1,

    selectedHighlightAncestor: true,

    clearSelectedAfterClickGroupNode: true,
    viewCenterAfterClick: [0.5, 0.5],

    render: {
      root: {
        item: {
          children: {
            rect: {
              style: {
                fill: '#fff',
                lineWidth: 1
              }
            },
            text: {
              style: {
                //
                fontFamily: 'PingFangSC-Medium',
                fontSize: 16,
                fontWeight: 500,
                align: 'center',
                verticalAlign: 'bottom',
                fill: 'rgba(0,0,0,0.84)'
              }
            },
            attachment: {
              style: {
                fontFamily: 'THSJinRongTi-Medium',
                fontSize: 12,
                fontWeight: 500,
                align: 'center',
                verticalAlign: 'top',
                //
                _fill: ['#07ab4b', 'rgba(0,0,0,.6)', '#FF2436']
              }
            }
          }
        }
      },
      normal: {
        item: {
          children: {
            rect: {
              style: {
                fill: '#fff',
                stroke: 'transparent',
                lineWidth: 1
              }
            },
            text: {
              style: {
                //
                fontFamily: 'PingFangSC-Medium',
                fontSize: 12,
                fontWeight: 500,
                align: 'center',
                verticalAlign: 'bottom',
                fill: 'rgba(0,0,0,0.84)'
              }
            },
            attachment: {
              style: {
                fontFamily: 'THSJinRongTi-Medium',
                fontSize: 10,
                fontWeight: 500,
                align: 'center',
                verticalAlign: 'top',
                //
                _fill: ['#07ab4b', 'rgba(0,0,0,.6)', '#FF2436']
              }
            }
          }
        },
        line: {
          children: {
            line: {
              style: {
                fill: 'transparent',
                stroke: '#979797',
                lineWidth: 1
              }
            }
          }
        }
      },
      group: {
        item: {
          children: {
            labelText: {
              style: {
                //
                fontFamily: 'PingFangSC-Medium',
                fontSize: 10,
                fontWeight: 500,
                align: 'center',
                verticalAlign: 'middle',
                fill: 'rgba(0,0,0,0.60)'
              }
            },
            labelRect: {
              style: {
                fill: '#f5f5f5',
                stroke: '#9C9C9C'
              }
            }
          }
        },
        line: {
          children: {
            line: {
              style: {
                fill: 'transparent',
                stroke: '#979797',
                lineWidth: 1
              }
            }
          }
        }
      }
    }
  };

  /**
   * Prevent from auto set z, zlevel, z2 by the framework.
   */
  preventAutoZ = true;

  layoutInfo: LayoutRect;

  leftTree: TreeType<TwoWayTreeSeriesModelClazz>;

  rightTree: TreeType<TwoWayTreeSeriesModelClazz>;

  /**
   * Init a tree data structure from data in option series
   */
  getInitialData(this: TwoWayTreeSeriesModelClazz, option: TwoWayTreeSeriesOption): SeriesData {
    const rootItem = option.data;
    const leftTreeItems: TwoWayTreeSeriesNodeItemOption[] = [];
    const rightTreeItems: TwoWayTreeSeriesNodeItemOption[] = [];

    (rootItem.children || []).slice().forEach(item => {
      if (item.position === 'left') {
        leftTreeItems.push(item);
      } else {
        rightTreeItems.push(item);
      }
    });

    // ! 根节点的深度为 1，约定深度为偶数时为普通节点，深度为奇数时为组节点，所以初始化的深度应该停在奇数位置上，否则不显示组节点的话就不能交互展开子节点了
    if (typeof option.initialTreeDepth !== 'undefined' && option.initialTreeDepth % 2 === 0) {
      option.initialTreeDepth += 1;
    }

    // create an virtual root
    //
    const leftRoot: TwoWayTreeSeriesNodeItemOption = {
      name: option.name as string,
      children: [{ ...rootItem, children: leftTreeItems, type: NODE_TYPE.ROOT }]
    };
    //
    const rightRoot: TwoWayTreeSeriesNodeItemOption = {
      name: option.name as string,
      children: [{ ...rootItem, children: rightTreeItems, type: NODE_TYPE.ROOT }]
    };

    this.leftTree = buildTree(leftRoot, this);
    this.rightTree = buildTree(rightRoot, this);

    setToInitialState(this);

    return this.leftTree.data;
  }

  //
}
